import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleLayoutProps {
  title: string;
  pageTitle: string;
  description: string;
  headline: string;
  breadcrumb: string;
  children: React.ReactNode;
  faqData?: { q: string; a: string }[];
}

export default function ArticleLayout({ title, pageTitle, description, headline, breadcrumb, children, faqData }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: title,
              description,
              author: { '@type': 'Organization', name: 'Itaobuy Spread' },
              datePublished: '2026-07-14',
              dateModified: '2026-07-14',
              publisher: { '@type': 'Organization', name: 'Itaobuy Spread' },
            }),
          }}
        />

        {faqData && faqData.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: faqData.map((faq) => ({
                  '@type': 'Question',
                  name: faq.q,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.a,
                  },
                })),
              }),
            }}
          />
        )}
        <div className="w-full px-6 md:px-10 py-4 bg-background-50 border-b border-background-200/60">
          <nav className="max-w-4xl mx-auto flex flex-wrap items-center gap-2 text-xs font-label text-foreground-400">
            <Link to="/" className="hover:text-foreground-700 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
            <span className="text-foreground-600">{breadcrumb}</span>
          </nav>
        </div>

        <section className="w-full py-12 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-50 border border-primary-100 mb-6">
              <span className="text-xs font-label font-medium text-primary-600 whitespace-nowrap">Articolo • Luglio 2026</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              {headline}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl mb-6">
              {description}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-400 font-label">
              <span>Pubblicato: 14 Luglio 2026</span>
              <span>Itaobuy Spread Team</span>
            </div>
          </div>
        </section>

        <article className="w-full py-8 md:py-12 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <p className="text-sm text-foreground-500 mb-8">
              <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium transition-colors">
                ← Torna alla Home di Itaobuy Spreadsheet
              </Link>
            </p>
            <div className="space-y-10">
              {children}
            </div>
            {faqData && faqData.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/60">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-5">Domande Frequenti</h2>
                <div className="space-y-3">
                  {faqData.map((faq, i) => (
                    <details key={i} className="group bg-background-100 rounded-xl border border-background-200/60">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-label text-sm font-medium text-foreground-800 list-none">
                        {faq.q}
                        <i className="ri-add-line group-open:rotate-45 transition-transform w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                      </summary>
                      <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <p className="text-sm text-foreground-500 mb-3">
                Per esplorare tutte le categorie e iniziare a utilizzare Itaobuy Spreadsheet:
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-label text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                Torna alla Home di Itaobuy Spreadsheet
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </article>

        <section className="w-full py-16 px-6 md:px-10 bg-background-100">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Pronto a scoprire Itaobuy Spreadsheet?
            </h2>
            <p className="text-foreground-500 text-sm mb-8">
              Accedi ora alla piattaforma completa e inizia a esplorare tutte le categorie.
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-white font-label text-base font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Visita Itaobuy Spreadsheet
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}